// ─────────────────────────────────────────────────────────────────────────────
// deltaCallGate.ts — THE THREE ANSWERS, AS THE BODY THE ROUTE TAKES.
//
// PURE. No fetch, no store, no DOM. `POST /api/delta/call-gate`
// (APP_CALLS_CONTRACT §4, `server/delta_gate.call_answer`):
//
//   Was the call made?  NO  → next_attempt_at (required)
//                       YES → Was it successful?
//                               NO  → done (declined)
//                               YES → agreed_datetime (required)
//   buyer_own_words — optional, only when the call was made.
//
// ── IT REFUSES LOCALLY ONLY WHAT THE SERVER REFUSES ────────────────────────
// no company · no task · a question left unanswered · no date · a date that
// cannot be read. Nothing stricter: a rule the server does not hold would be a
// limit nobody authorised. The server still checks everything again.
//
// ── A FIELD THE ANSWER DOES NOT REACH IS NEVER SENT ────────────────────────
// The route refuses `successful`, `agreed_datetime` or `buyer_own_words` beside
// `reached: false` ("nobody heard anything"). The body is BUILT from the
// branch taken, so words typed before the owner changed an answer cannot ride
// along. And an empty `buyer_own_words` is left out, not sent as "": an absent
// field and an empty one are different claims.
// ─────────────────────────────────────────────────────────────────────────────

import { localPickerToIso } from './deltaGate';
import { CallRow } from './deltaCallRow';

export interface CallGateAnswers {
  /** Question one. `null` = not answered yet. */
  made: boolean | null;
  /** Question two — asked only when `made`. */
  successful: boolean | null;
  /** `datetime-local` values, exactly as the pickers hand them back. */
  nextAttemptLocal: string;
  agreedLocal: string;
  words: string;
}

export const NO_ANSWERS: CallGateAnswers = {
  made: null, successful: null, nextAttemptLocal: '', agreedLocal: '', words: '',
};

export interface CallGateBody {
  company_id: string;
  slug?: string;
  sales_task_id: string;
  call_id?: string;
  reached: boolean;
  next_attempt_at?: string;
  successful?: boolean;
  agreed_datetime?: string;
  buyer_own_words?: string;
}

export interface CallGateRefusal {
  refused: true;
  ground: string;
  message: string;
}

export function isCallGateRefusal(r: CallGateBody | CallGateRefusal): r is CallGateRefusal {
  return (r as CallGateRefusal).refused === true;
}

const refuse = (ground: string, message: string): CallGateRefusal =>
  ({ refused: true, ground, message });

/** A picker value → ISO with the browser's offset, or a refusal naming it. */
function when(local: string, at?: Date): string | CallGateRefusal {
  if (!String(local || '').trim()) return refuse('no_datetime', 'pick the date and time');
  const iso = localPickerToIso(local, at);
  return iso || refuse('bad_datetime', 'that date and time could not be read');
}

/** `at` is a test seam for the offset; the live screen never passes it. */
export function buildCallGateBody(
  row: CallRow | null, answers: CallGateAnswers, at?: Date,
): CallGateBody | CallGateRefusal {
  if (!row || !row.companyId) return refuse('no_company', 'press a call in the list first');
  if (!row.salesTaskId) return refuse('no_task', 'this row carries no task to answer');
  if (answers.made !== true && answers.made !== false) {
    return refuse('no_answer', 'was the call made?');
  }
  const base: CallGateBody = {
    company_id: row.companyId,
    sales_task_id: row.salesTaskId,
    reached: answers.made,
  };
  if (row.slug) base.slug = row.slug;
  if (row.callId) base.call_id = row.callId;

  if (!answers.made) {
    const next = when(answers.nextAttemptLocal, at);
    if (typeof next !== 'string') return next;
    return { ...base, next_attempt_at: next };
  }

  if (answers.successful !== true && answers.successful !== false) {
    return refuse('no_answer', 'was it successful?');
  }
  const out: CallGateBody = { ...base, successful: answers.successful };
  const words = String(answers.words || '').trim();
  if (words) out.buyer_own_words = words;
  if (!answers.successful) return out;

  const agreed = when(answers.agreedLocal, at);
  if (typeof agreed !== 'string') return agreed;
  return { ...out, agreed_datetime: agreed };
}

// ── THE ANSWER, READ ───────────────────────────────────────────────────────

export interface Receipt {
  name: string;
  written: boolean;
  /** The receipt's own ground when it was not written. Shown as given. */
  ground: string;
}

export type CallGateResult =
  | { phase: 'written'; event: string; unwritten: Receipt[] }
  | { phase: 'refused'; ground: string; error: string }
  | { phase: 'failed'; error: string };

/** The route's response → what the gate shows. `status` is the HTTP status;
 *  `body` is the parsed JSON, or `null` when it would not parse. */
export function readCallGateResponse(status: number, body: unknown): CallGateResult {
  const b = (body && typeof body === 'object' ? body : {}) as Record<string, unknown>;
  const s = (v: unknown) => (v == null ? '' : String(v));
  if (status === 200 && b.success === true) {
    const receipts = (b.receipts && typeof b.receipts === 'object' ? b.receipts : {}) as
      Record<string, unknown>;
    // A RECEIPT WITH `written: false` IS SHOWN, NOT HIDDEN — and it does not make
    // the whole answer a failure: the others landed.
    const unwritten: Receipt[] = Object.keys(receipts)
      .map((name) => {
        const r = (receipts[name] && typeof receipts[name] === 'object'
          ? receipts[name] : {}) as Record<string, unknown>;
        return { name, written: r.written === true, ground: s(r.ground) };
      })
      .filter((r) => !r.written);
    return { phase: 'written', event: s(b.event), unwritten };
  }
  if (b.refused === true) {
    return { phase: 'refused', ground: s(b.ground), error: s(b.error) };
  }
  return { phase: 'failed', error: s(b.error) || (status ? 'the server answered ' + status : 'no answer from the server') };
}
